/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      component (embedded login / check-mail form for the checkout flow)
 *   extends:   Component (@glimmer/component)
 *   template:  inline-login.hbs
 *   args:      @applicationController, @redirect_id
 *   uses:      services session, router, notify, intl, log;
 *              beebee-base/lib/get-config; beebee-base/lib/utility/ajax
 *              (beebeeAjaxPost, bbParam)
 *   siblings:  -
 *   tests:     inline-login-writes-test.js, storefront-components-test.js
 *   gotchas:
 *     - loginTask delegates all post-login work to session.afterLoginTask
 *       (authenticate, load /me, configure UI, reset showLogin, run
 *       taskAfterLogin, router.refresh) — nothing must run after that call
 *       since the re-render it triggers can destroy this component.
 *
 * OAuth password-grant login plus a "check mail" lookup, used inline on
 * checkout/checkin routes instead of a dedicated login page.
 */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';

import { task } from 'ember-concurrency';

import { service } from '@ember/service';

import getConfig from 'beebee-base/lib/get-config';
import { beebeeAjaxPost, bbParam } from 'beebee-base/lib/utility/ajax';

export default class InlineLoginComponent extends Component {
  @service session;
  @service router;
  @service notify;
  @service intl;
  @service log;

  @tracked targetGoogle = '_self';
  @tracked accesso = null;
  @tracked password = null;
  @tracked email = null;

  @action
  setup(/*element*/) {
    if (window.location !== window.parent.location) {
      this.targetGoogle = '_blank';
    } else {
      this.targetGoogle = '_self';
    }
  }

  @action
  submitLogin(e) {
    e.preventDefault();
    this.loginTask.perform();
  }

  @action
  submitCheckMail(e) {
    e.preventDefault();
    this.checkMailTask.perform();
  }

  @task
  *loginTask() {
    var data = {
      grant_type: 'password',
      username: this.accesso?.dati?.username ? this.accesso.dati.username.trim() : null,
      password: this.password ? this.password.trim() : null,
    };

    try {
      const response = yield fetch(`${getConfig(this).APP.urlApi}/../oauth/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          Authorization: 'Basic YWJjMTIzOnNzaC1zZWNyZXQ=',
        },
        body: bbParam(data),
      });

      if (!response.ok) {
        throw new Error('login failed');
      }

      var accesso = yield response.json();

      if (accesso.access_token) {
        // Delega tutto al session service: autentica, carica /me, configura UI,
        // resetta showLogin, esegue taskAfterLogin e forza router.refresh().
        // NON mettere logica post-login dopo questo: il componente potrebbe
        // essere distrutto dal re-render.
        this.session.afterLoginTask.perform(accesso.access_token, {
          controller: this.args.applicationController,
          taskAfterLogin: this.args.applicationController?.loadDataTask,
        });

        if (this.args.redirect_id) {
          this.router.transitionTo('detail', this.args.redirect_id);
        }
      } else {
        this.accesso = { ...this.accesso, error: true };
      }
    } catch (e) {
      // Null-safe: spread of null yields {}; tracked reassignment updates the template.
      this.accesso = { ...this.accesso, error: true };
    }
  }

  @task
  *checkMailTask() {
    try {
      var accesso = yield beebeeAjaxPost(
        `${getConfig(this).APP.urlApi}/check_mail`,
        null,
        {
          mail: this.email,
        }
      );

      this.accesso = {
        effettuato: true,
        dati: accesso,
      };
    } catch (e) {
      // Left uncaught, a 4xx/network failure here surfaced as a Sentry
      // unhandled rejection. User-triggered (check-mail form submit): notify.
      this.log.error('checkMailTask failed', e);
      const detail = e?.responseJSON?.errors?.[0]?.detail;
      this.notify.error(detail || this.intl.t('beebeeboard error'));
      return;
    }
  }
}
